import { useState, useEffect, useCallback, useMemo } from 'react';
import { FilterSchema, FilterState } from '../types';

interface ActiveFacetDisplay {
  key: string;
  label: string;
  value?: string;
}

/**
 * Custom hook for managing filter state with URL synchronization
 * Based on carrot-filtering-spec.md URL Contract
 */
export function useFilters(schema: FilterSchema) {
  // Initialize state from URL query params
  const [filters, setFilters] = useState<FilterState>(() => {
    const params = new URLSearchParams(window.location.search);
    const initialState: FilterState = {};

    schema.facets.forEach((facet) => {
      const paramKey = facet.param ?? facet.id;
      if (!paramKey) {
        return;
      }
      const value = params.get(paramKey);
      if (value) {
        // Handle multi-select (comma-separated values)
        if (facet.type === 'select' && facet.multi) {
          initialState[facet.id] = value.split(',');
        } else {
          initialState[facet.id] = value;
        }
      }
    });

    return initialState;
  });

  // Sync filters to URL whenever they change
  useEffect(() => {
    // Preserve unrelated query params but replace known filter params
    const params = new URLSearchParams(window.location.search);

    // Remove existing filter params to avoid stale state
    schema.facets.forEach((facet) => {
      const paramKey = facet.param ?? facet.id;
      if (paramKey) {
        params.delete(paramKey);
      }
    });

    Object.entries(filters).forEach(([key, value]) => {
      const facet = schema.facets.find((f) => f.id === key);
      const paramKey = facet?.param ?? key;

      if (!paramKey || value === undefined || value === '' || value === null) {
        return;
      }

      if (Array.isArray(value)) {
        // Multi-select: join with comma
        if (value.length > 0) {
          params.set(paramKey, value.join(','));
        }
      } else {
        params.set(paramKey, String(value));
      }
    });

    // Update URL without page reload
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.replaceState({}, '', newUrl);
  }, [filters, schema]);

  // Update a single filter
  const setFilter = useCallback((key: string, value: string | string[] | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Clear a single filter (state only)
  const clearFilter = useCallback((key: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Remove filter from URL and navigate (chip "X")
  const removeFilter = useCallback((key: string) => {
    const facet = schema.facets.find((f) => f.id === key);
    const paramKey = facet?.param ?? facet?.id;
    if (!facet || !paramKey) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    params.delete(paramKey);

    const qs = params.toString();
    const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.location.assign(next);
  }, [schema]);

  // Clear all filters
  const clearAll = useCallback(() => {
    setFilters({});
  }, []);

  // Derive active filter chips with schema labels/options
  const activeFilters = useMemo<ActiveFacetDisplay[]>(() => {
    const chips: ActiveFacetDisplay[] = [];

    schema.facets.forEach((facet) => {
      const id = facet.id;
      if (!id) return;

      const raw = filters[id];
      if (raw === undefined || raw === '' || raw === null) {
        return;
      }

      const label = facet.labelKey || id;
      let display: string;

      if (Array.isArray(raw)) {
        display = raw.join(', ');
      } else {
        display = String(raw);
      }

      if (!Array.isArray(raw) && facet.type === 'select' && facet.options) {
        const match = facet.options.find((opt) => opt.value === raw);
        if (match?.labelKey) {
          display = match.labelKey;
        }
      }

      chips.push({
        key: id,
        label,
        value: display,
      });
    });

    return chips;
  }, [filters, schema]);

  // Count active filters
  const activeCount = activeFilters.length;

  return {
    filters,
    setFilter,
    clearFilter,
    removeFilter,
    clearAll,
    activeFilters,
    activeCount,
  };
}
